/**
 * mapBrand — canonical brand accent для map-элементов (контуры полей,
 * focused highlights, map CTA-link).
 *
 * AK-297 (design review 2026-06-27): контуры полей переведены с легаси-оранжа
 * на Earth Tech BRAND-зелёный. Бренд-зелёный = семантический `--primary`
 * (= `--palette-primary-600` = hsl(142 76% 36%), Earth Tech green). MapLibre
 * paint expressions требуют raw hex — `hsl(var(--token))` не парсится GL style
 * spec, поэтому хекс whitelisted (CLAUDE.md §UI-first agents protocol →
 * `frontend-vite/src/lib/colorMaps/`). Значение синхронизировано с tokens.css
 * `--palette-primary-600`; при ребрендинге обновлять оба места.
 *
 * R4: вынесен из inline-const в `lib/map/layers/registry.ts` в общий
 * whitelisted colorMaps, чтобы бренд-акцент карты был единым источником.
 */
export const MAP_BRAND_GREEN = '#16A34A';

/**
 * MAP_NO_DATA_GREY — нейтральный «нет данных» для MapLibre paint-выражений.
 *
 * AK-516 R2 (нит head-web): тот же хекс был продублирован трижды в
 * `lib/map/layers/registry.ts` (avg-ndvi / uniformity / crop-fill) и ещё раз в
 * `lib/map/effects/cropPatternAtlasEffect.ts` (`FALLBACK_GREY`). Хекс-литералы
 * в whitelisted `lib/colorMaps/` (CLAUDE.md §UI-first agents protocol) — здесь
 * им и место; тот же приём, что R4 сделал с `MAP_BRAND_GREEN`.
 *
 * Значение синхронизировано с `SLOPE_NO_DATA_COLOR` (`lib/colorMaps/slopeColors.ts`)
 * и с fallback'ом NDVI-палитры — «нет данных» на карте выглядит одинаково.
 */
export const MAP_NO_DATA_GREY = '#9CA3AF';

/**
 * MAP_OUTLINE_CASING — тёмная подложка-обводка («casing») под контуром поля.
 *
 * UAT founder на staging v1.1637: «границы полей еле видны на спутнике».
 * Корень читаемости — не цвет линии, а КОНТРАСТ с пёстрой подложкой: яркая
 * линия шириной 1–2 px тонет и в светлой стерне, и в тёмной пашне, потому что
 * фон под ней меняется на каждом метре. Классический картографический приём —
 * casing: широкая тёмная линия под узкой яркой, которая даёт линии собственный
 * фон и не зависит от снимка (тот же принцип, что halo у подписей).
 *
 * Значение = `--palette-neutral-900` (Earth Tech warm stone) из
 * `styles/tokens.css` — тот же тёмный нейтральный, что у текста подписей-пилюль;
 * рисуется с полупрозрачностью (см. `registry.ts`), чтобы не превращаться в
 * жирную чёрную кайму на обзорных зумах.
 */
export const MAP_OUTLINE_CASING = '#1C1917';

/**
 * MAP_OUTLINE_FIELD — боевой цвет контура посевного поля (`outline-planting`).
 *
 * Решение founder (AK-603, 2026-08-15): из 4 кандидатов (коралл/циан/лайм/
 * маджента, см. историю `mapContourVariants.ts` до этого коммита) выбрана
 * НЕОНОВАЯ МАДЖЕНТА — критерий founder'а: цвет должен быть далёк и от
 * NDVI-палитры (красный↔жёлтый↔зелёный, `ndvi_color_palette`), и от
 * естественных цветов спутника (вода/зелень/почва). Маджента лежит вне обеих
 * осей и остаётся отличима от жёлтого выделения `#FACC15`.
 *
 * Раньше цвет приходил per-field через MVT `outline_color`, но в
 * `field_visual_attrs` это мёртвая константа `'#FF6D00'` (миграции
 * 083/087/097/115/120/151 + tenant_template.sql — одинаково во всех ветках
 * MV, семантики не несёт). Фронт забирает цвет себе константой; мёртвый
 * столбец MV чистится отдельно, когда домен будет трогать MV.
 */
export const MAP_OUTLINE_FIELD = '#FF00C8';

/**
 * MAP_SELECTION_YELLOW — цвет выделения выбранного поля на карте.
 *
 * AK-590: раньше хекс жил инлайном в `field-selected-highlight` (line-color) и
 * должен был появиться вторым местом в `pillSprite` (бордер пилюли выбранного
 * поля). Один источник — здесь, в whitelisted colorMaps.
 *
 * Tailwind yellow-400. Используется ТОЛЬКО для выделения (контур + бордер
 * пилюли выбранного поля), чтобы жёлтый не конкурировал с бренд-зелёным
 * контуром за внимание.
 */
export const MAP_SELECTION_YELLOW = '#FACC15';
